/**
 * CheatGuideShell — reusable collapsible "how to" panel.
 *
 * Each module gets one of these at the top of its panel.  State (open vs.
 * collapsed) persists per-browser via localStorage so the team can hide it
 * once they've onboarded.
 *
 * Also provides matching primitives: <Section>, <Btn>, <Pill>, <Choice>
 * and a helper Row for tables, so individual guides stay short and
 * consistent.
 */
import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

export default function CheatGuideShell({ title, storageKey, defaultOpen = true, testId, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    setOpen(o => {
      const next = !o;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch {}
      return next;
    });
  };
  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ borderColor: '#E2D8C2', background: '#FFF8EC' }}
      data-testid={testId || 'cheat-guide'}>
      <button onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#FBF1D8]/60 transition"
        data-testid={(testId || 'cheat-guide') + '-toggle'}>
        <div className="flex items-center gap-2">
          <HelpCircle size={15} style={{ color: '#7A5C2B' }} />
          <span className="text-[13px] font-semibold" style={{ color: '#7A5C2B' }}>
            {title}
          </span>
        </div>
        {open ? <ChevronUp size={14} style={{ color: '#7A5C2B' }} />
              : <ChevronDown size={14} style={{ color: '#7A5C2B' }} />}
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 space-y-4 text-[12.5px]" style={{ color: '#3D3528' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ------------- inline content primitives -------------
export function Section({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 font-semibold mb-1.5"
        style={{ color: '#5C4F3F' }}>
        {icon} {title}
      </div>
      <div className="pl-1 space-y-1.5">{children}</div>
    </div>
  );
}

export function Choice({ label, recommended, children }) {
  return (
    <div className="flex items-start gap-2 flex-wrap">
      <span className="font-semibold whitespace-nowrap" style={{ color: '#1B2A4A' }}>
        {label}{recommended && <span style={{ color: '#7A5C2B' }}> ★</span>}
      </span>
      <span style={{ color: '#3D3528' }}>— {children}</span>
    </div>
  );
}

export function Btn({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border align-middle text-[11px]"
      style={{ borderColor: '#E5E1DC', background: '#FFFFFF', color: '#1B2A4A' }}>
      {icon} {label}
    </span>
  );
}

export function Pill({ bg, fg, children }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ background: bg, color: fg }}>
      {children}
    </span>
  );
}

export function Warn({ children }) {
  return (
    <div className="mt-2 p-2 rounded text-[11.5px]"
      style={{ background: '#FEF2EE', color: '#92400E', border: '1px solid #FBD0BC' }}>
      ⚠️ {children}
    </div>
  );
}

export function Note({ children, color = '#FFFFFFAA' }) {
  return (
    <div className="mt-2 p-2 rounded text-[11.5px]"
      style={{ background: color, color: '#5C4F3F' }}>
      {children}
    </div>
  );
}
